import Table from "@mui/joy/Table";
import Grid from "@mui/joy/Grid";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Box from "@mui/joy/Box";
import Tooltip from "@mui/joy/Tooltip";
import { useMemo } from "react";
import { ARInvoiceDetailAttributes, GetInvoiceDetailsResponseObj, IFInvoiceDetailsObj } from "@/types/interfaces/crm";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import Loader from "@/components/Loader/Loader";
import ArticleIcon from "@mui/icons-material/Article";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import LocalConvenienceStoreIcon from "@mui/icons-material/LocalConvenienceStore";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import StarIcon from "@mui/icons-material/Star";
import EventIconOutlined from "@mui/icons-material/EventOutlined";
import NumbersIcon from "@mui/icons-material/Numbers";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import copy from "copy-text-to-clipboard";
import { toast } from "sonner";
import { ProductCodesMap } from "@/types/constants/productCodes";
import { customCurrencyFormatter } from "@/utils/currencyFormatter";
import { SaleTypeMap } from "@/types/constants/saleType";
import { VoucherStatusMap } from "@/types/constants/voucherStatus";
import { TransactionTypeMap } from "@/types/constants/transactionType";
import { ValidationResponse } from "../../utility/validationChecker";
import { risingStarMap } from "@/types/constants/risingStarMap";
import { risingStarData } from "@/types/constants/risingStarData";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import { getAgentRisingStarLevel } from "@/services/api/interface-validator/risingStar";
import { formatInTorontoTime, resolveProductTier } from "../../utility/utils";

const bookingOverviewFields: {
  field: string;
  access: (
    data: GetInvoiceDetailsResponseObj["data"]["reservations"][number],
    references?: GetInvoiceDetailsResponseObj["references"],
  ) => string | number;
  Icon: any;
}[] = [
  {
    field: "Vat code",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) => data.reservation.vatCode || "_",
    Icon: ArticleIcon,
  },
  {
    field: "No. of Days",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) =>
      data.reservation.numberOfDays?.toString() || "_",
    Icon: CalendarViewMonthIcon,
  },
  {
    field: "No. of Passengers",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) =>
      data.reservation.numberOfPassengers?.toString() || "_",
    Icon: PeopleIcon,
  },
  // {
  //   field: "Free field A",
  //   access: (data: GetInvoiceDetailsResponseObj['data']['reservations'][number]) => data.reservation.freeFieldA || "_",
  //   Icon: CommentIcon,
  // },
  {
    field: "Voucher status",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) =>
      VoucherStatusMap[data.ifInvoiceDetails?.[0].voucherStatus] || "_",
    Icon: ConfirmationNumberIcon,
  },
  {
    field: "Product Code",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) =>
      `${data.reservation.productCode} - ${ProductCodesMap[data.reservation.productCode]}`,
    Icon: LocalConvenienceStoreIcon,
  },
  {
    field: "Currency Code",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) =>
      data.reservation.currencyCode || "_",
    Icon: CurrencyExchangeOutlinedIcon,
  },
  {
    field: "Sale Type",
    access: (
      data: GetInvoiceDetailsResponseObj["data"]["reservations"][number],
      references?: GetInvoiceDetailsResponseObj["references"],
    ) => {
      const saleType = references?.saleTypes.find(
        (type) =>
          type.profileNumber === data.reservation.vendorNumber && type.productCode === data.reservation.productCode,
      );
      return saleType?.saleType !== undefined ? SaleTypeMap[saleType.saleType] : "_";
    },
    Icon: ArticleIcon,
  },
  {
    field: "Transaction Type",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) =>
      TransactionTypeMap[data.ifInvoiceDetails?.[0].transactionType] || "_",
    Icon: NumbersIcon,
  },
  {
    field: "Resv. Date",
    access: (data: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) =>
      data.reservation.reservationDate
        ? formatInTorontoTime(new Date(data.reservation.reservationDate), "dd/MM/yyyy")
        : "_",
    Icon: EventIconOutlined,
  },
];

export default function ReservationLines({
  invoiceDetailsQueryClient,
  checkResults,
}: {
  invoiceDetailsQueryClient: UseQueryResult;
  checkResults: ValidationResponse[];
}) {
  const invoiceDetailsQueryData = invoiceDetailsQueryClient.data as unknown as GetInvoiceDetailsResponseObj;
  const tableRows = useMemo(() => {
    if (!invoiceDetailsQueryData) return [];
    const tableRows: any[] = [];
    invoiceDetailsQueryData?.data?.reservations?.forEach(
      (reservation: GetInvoiceDetailsResponseObj["data"]["reservations"][number]) => {
        const tableEntry: any = {
          tableRows: [],
          reservation: reservation.reservation,
          ifInvoiceDetails: reservation.ifInvoiceDetails,
          errors: checkResults
            .flatMap(
              (check) =>
                check.affectedLines?.map((line) =>
                  line.reservationId === reservation.reservation.reservationId ? line : null,
                ),
            )
            .filter(Boolean),
        };

        const reservationLine = {
          ...reservation.reservation,
          type: "Resv.",
          grossAmount: Number(
            (reservation.reservation.sellingFare || 0) +
              (reservation.reservation.tax1 || 0) +
              (reservation.reservation.tax2 || 0) +
              (reservation.reservation.tax3 || 0) +
              (reservation.reservation.tax4 || 0),
          ).toFixed(2),
          netCommission:
            Number(reservation.reservation.commissionAmount || 0) +
            (Number(reservation.reservation.sellingFare || 0) - Number(reservation.reservation.publishedFare || 0)),
        };
        tableEntry.tableRows.push(reservationLine);

        reservation.arInvoiceDetails?.forEach(
          (
            arInvoiceDetail: GetInvoiceDetailsResponseObj["data"]["reservations"][number]["arInvoiceDetails"][number],
          ) => {
            const arInvoiceDetailLine = {
              ...arInvoiceDetail,
              type: "AR",
              payment: {
                amount: arInvoiceDetail.payments.reduce((acc, payment) => acc + (payment.amount || 0), 0),
              },
              netCommission:
                Number(arInvoiceDetail.commissionAmount || 0) +
                (Number(arInvoiceDetail.sellingFare) - Number(arInvoiceDetail.publishedFare || 0)),
            };
            tableEntry.tableRows.push(arInvoiceDetailLine);
          },
        );

        reservation.ifInvoiceDetails.forEach(
          (
            ifInvoiceDetail: GetInvoiceDetailsResponseObj["data"]["reservations"][number]["ifInvoiceDetails"][number],
          ) => {
            const ifInvoiceDetailLine = {
              ...ifInvoiceDetail,
              type: "IF",
              payment: {
                amount: ifInvoiceDetail.payments.reduce((acc, payment) => acc + (payment.amount || 0), 0),
              },
              netCommission:
                Number(ifInvoiceDetail.commissionAmount || 0) +
                (Number(ifInvoiceDetail.sellingFare) - Number(ifInvoiceDetail.publishedFare || 0)),
            };
            tableEntry.tableRows.push(ifInvoiceDetailLine);
          },
        );

        const balanceRow = {
          type: "Bal.",
          publishedFare: Number(
            reservation.reservation.publishedFare -
              reservation.arInvoiceDetails?.reduce(
                (acc: number, arInvoiceDetail: any) => acc + arInvoiceDetail.publishedFare,
                0,
              ) -
              reservation.ifInvoiceDetails?.reduce(
                (acc: number, ifInvoiceDetailLine: any) => acc + ifInvoiceDetailLine.publishedFare,
                0,
              ),
          ).toFixed(2),
          sellingFare: Number(
            reservation.reservation.sellingFare -
              reservation.arInvoiceDetails?.reduce(
                (acc: number, arInvoiceDetail: any) => acc + arInvoiceDetail.sellingFare,
                0,
              ) -
              reservation.ifInvoiceDetails?.reduce(
                (acc: number, ifInvoiceDetailLine: any) => acc + ifInvoiceDetailLine.sellingFare,
                0,
              ),
          ).toFixed(2),
          commissionAmount: Number(
            reservation.reservation.commissionAmount -
              reservation.arInvoiceDetails?.reduce(
                (acc: number, arInvoiceDetail: any) => acc + arInvoiceDetail.commissionAmount,
                0,
              ) -
              reservation.ifInvoiceDetails?.reduce(
                (acc: number, ifInvoiceDetailLine: any) => acc + ifInvoiceDetailLine.commissionAmount,
                0,
              ),
          ).toFixed(2),
          vatOnCommission: Number(
            reservation.reservation.vatOnCommission -
              reservation.arInvoiceDetails?.reduce(
                (acc: number, arInvoiceDetail: any) => acc + arInvoiceDetail.vatOnCommission,
                0,
              ) -
              reservation.ifInvoiceDetails?.reduce(
                (acc: number, ifInvoiceDetailLine: any) => acc + ifInvoiceDetailLine.vatOnCommission,
                0,
              ),
          ).toFixed(2),
          osAgentCommission1: Number(
            reservation.reservation.osAgentCommission1 -
              reservation.arInvoiceDetails?.reduce(
                (acc: number, arInvoiceDetail: any) => acc + arInvoiceDetail.osAgentCommission1,
                0,
              ) -
              reservation.ifInvoiceDetails?.reduce(
                (acc: number, ifInvoiceDetailLine: any) => acc + ifInvoiceDetailLine.osAgentCommission1,
                0,
              ),
          ).toFixed(2),
          osAgentCommission2: Number(
            reservation.reservation.osAgentCommission2 -
              reservation.arInvoiceDetails?.reduce(
                (acc: number, arInvoiceDetail: any) => acc + arInvoiceDetail.osAgentCommission2,
                0,
              ) -
              reservation.ifInvoiceDetails?.reduce(
                (acc: number, ifInvoiceDetailLine: any) => acc + ifInvoiceDetailLine.osAgentCommission2,
                0,
              ),
          ).toFixed(2),
          tax1: Number(
            (reservation.reservation.tax1 || 0) -
              (reservation.arInvoiceDetails ?? []).reduce(
                (acc: number, arInvoiceDetail: ARInvoiceDetailAttributes) => acc + (arInvoiceDetail.tax1 || 0),
                0,
              ) -
              reservation.ifInvoiceDetails?.reduce(
                (acc: number, ifInvoiceDetailLine: any) => acc + (ifInvoiceDetailLine.tax1 || 0),
                0,
              ),
          ).toFixed(2),
          tax2:
            (reservation.reservation.tax2 || 0) -
            (reservation.arInvoiceDetails ?? []).reduce(
              (acc: number, arInvoiceDetail: ARInvoiceDetailAttributes) => acc + (arInvoiceDetail.tax2 || 0),
              0,
            ) -
            reservation.ifInvoiceDetails?.reduce(
              (acc: number, ifInvoiceDetailLine: any) => acc + (ifInvoiceDetailLine.tax2 || 0),
              0,
            ),
          tax3:
            (reservation.reservation.tax3 || 0) -
            (reservation.arInvoiceDetails ?? []).reduce(
              (acc: number, arInvoiceDetail: ARInvoiceDetailAttributes) => acc + (arInvoiceDetail.tax3 || 0),
              0,
            ) -
            reservation.ifInvoiceDetails?.reduce(
              (acc: number, ifInvoiceDetailLine: any) => acc + (ifInvoiceDetailLine.tax3 || 0),
              0,
            ),
          tax4:
            (reservation.reservation.tax4 || 0) -
            (reservation.arInvoiceDetails ?? []).reduce(
              (acc: number, arInvoiceDetail: ARInvoiceDetailAttributes) => acc + (arInvoiceDetail.tax4 || 0),
              0,
            ) -
            reservation.ifInvoiceDetails?.reduce(
              (acc: number, ifInvoiceDetailLine: any) => acc + (ifInvoiceDetailLine.tax4 || 0),
              0,
            ),
          grossAmount: tableEntry.tableRows
            .reduce((acc: number, row: any) => {
              if (row.type == "Resv.") {
                return acc - Number(row.grossAmount || 0);
              } else {
                return acc + Number(row.grossAmount || 0);
              }
            }, 0)
            .toFixed(2),
          payment: {
            amount:
              reservation.arInvoiceDetails.reduce(
                (acc: number, arInvoiceDetail: ARInvoiceDetailAttributes) => acc + (arInvoiceDetail?.grossAmount || 0),
                0,
              ) +
              reservation.ifInvoiceDetails.reduce(
                (acc: number, ifInvoiceDetailLine: IFInvoiceDetailsObj) =>
                  acc + (ifInvoiceDetailLine?.grossAmount || 0),
                0,
              ) -
              (reservation.arInvoiceDetails.reduce(
                (
                  acc: number,
                  arInvoiceDetail: GetInvoiceDetailsResponseObj["data"]["reservations"][number]["arInvoiceDetails"][number],
                ) => acc + (arInvoiceDetail?.payments.reduce((acc, payment) => acc + (payment.amount || 0), 0) || 0),
                0,
              ) +
                reservation.ifInvoiceDetails.reduce(
                  (
                    acc: number,
                    ifInvoiceDetailLine: GetInvoiceDetailsResponseObj["data"]["reservations"][number]["ifInvoiceDetails"][number],
                  ) =>
                    acc + (ifInvoiceDetailLine?.payments.reduce((acc, payment) => acc + (payment.amount || 0), 0) || 0),
                  0,
                )),
          },
        };

        tableEntry.tableRows.push(balanceRow);
        tableRows.push(tableEntry);
      },
    );
    return tableRows;
  }, [invoiceDetailsQueryClient.data, checkResults]);

  const primaryAgentNumber =
    invoiceDetailsQueryData?.data?.trip.osAgentNumber2 || invoiceDetailsQueryData?.data?.trip.osAgentNumber1;
  const productTiers = invoiceDetailsQueryData?.references?.productTiers || [];

  const risingStarQuery = useQuery({
    queryKey: ["risingStarLevel", primaryAgentNumber],
    enabled: !!primaryAgentNumber,
    queryFn: () => getAgentRisingStarLevel(primaryAgentNumber!),
  });

  const risingStarLevel = risingStarQuery.data?.risingStarLevel ?? undefined;

  return (
    <>
      <>
        {tableRows.map((row: any, index: number) => {
          const reservationDateYear = new Date(row.reservation?.reservationDate || "").getFullYear();
          const currentYear = new Date().getFullYear();
          const agentId = parseInt(primaryAgentNumber || "");

          let effectiveRisingStarLevel: number | undefined;
          if (reservationDateYear === currentYear) {
            effectiveRisingStarLevel = risingStarLevel;
          } else if (reservationDateYear === 2024 || reservationDateYear === 2025) {
            const entry = risingStarData[agentId];
            effectiveRisingStarLevel = entry
              ? entry[`y${reservationDateYear}` as "y2024" | "y2025"] ?? undefined
              : undefined;
          } else {
            effectiveRisingStarLevel = undefined;
          }

          return (
            <Stack
              key={index}
              id={`reservation-id-${row?.reservation?.reservationId}`}
              sx={{
                width: "100%",
                border: "1px solid #e0e0e0",
                borderRadius: 10,
                marginBottom: "2rem",
              }}
            >
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                sx={{ padding: "1rem", borderBottom: "1px solid #e0e0e0", borderRadius: "10px 10px 0 0" }}
              >
                <Typography fontFamily={"sans-serif"} fontWeight={500} fontSize={16} textColor="#334155">
                  #{parseInt(row?.reservation?.vendorNumber)} | {row?.reservation?.vendorName} -{" "}
                  {row.reservation?.ticketNumber || row.reservation?.recordLocator || "N/A"}
                </Typography>
                {(() => {
                  const bookingNumber = row.reservation?.ticketNumber || row.reservation?.recordLocator;
                  if (!bookingNumber) return null;
                  return (
                    <Tooltip variant="outlined" title="Copy booking number" arrow>
                      <IconButton
                        variant="plain"
                        color="neutral"
                        size="sm"
                        onClick={() => {
                          if (copy(bookingNumber)) {
                            toast.success("Booking number copied to your clipboard!");
                          }
                        }}
                      >
                        <ContentCopyOutlinedIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Tooltip>
                  );
                })()}
                {(() => {
                  const productTierObj = productTiers?.find((pt) => row.reservation?.vendorNumber === pt.vendorNumber);
                  const productTier = resolveProductTier(productTierObj, row.reservation?.productCode).toLowerCase();
                  if (productTier === "preferred") {
                    return <Chip>Preferred</Chip>;
                  }
                  if (productTier === "approved") {
                    return <Chip>Approved</Chip>;
                  }
                  if (productTier === "others") {
                    return <Chip>Others</Chip>;
                  }
                  return null;
                })()}
                {row.errors?.length > 0 && (
                  <Tooltip
                    arrow
                    disableHoverListener={row.errors?.length === 1}
                    variant="outlined"
                    title={
                      <Stack
                        spacing={1}
                        sx={{
                          width: "30rem",
                          padding: "1rem",
                          boxShadow: "box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;",
                          bgcolor: "#fff",
                        }}
                      >
                        {row.errors?.map((err: any, index: number) => (
                          <Typography
                            fontFamily={"Inter"}
                            key={index}
                            sx={{
                              fontSize: "16px",
                            }}
                          >
                            {index + 1}. {err?.message}
                          </Typography>
                        ))}
                      </Stack>
                    }
                  >
                    <Stack
                      direction="row"
                      gap={0.5}
                      alignItems={"center"}
                      sx={{
                        backgroundColor: "#FEF2F2",
                        padding: "0.2rem 0.5rem",
                        borderRadius: "8px",
                      }}
                    >
                      <ErrorOutlineOutlinedIcon color="danger" sx={{ fontSize: "16px" }} />
                      <Typography fontSize={"14px"} color="danger" fontFamily={"Inter"}>
                        {row.errors?.length === 1 ? row.errors[0]?.message : `${row.errors?.length} Checks Failed`}
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
              </Stack>
              {invoiceDetailsQueryClient.isRefetching ? (
                <Box sx={{ padding: "2rem" }}>
                  <Loader />
                </Box>
              ) : (
                <Box>
                  <Grid container spacing={4} padding={3}>
                    {bookingOverviewFields.map((field) => (
                      <Grid xs={2.4} key={field.field}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Stack
                            sx={{
                              width: "35px",
                              height: "35px",
                              backgroundColor: "#F1F5F9",
                              borderRadius: "10px",
                              padding: "8px",
                            }}
                          >
                            <field.Icon sx={{ height: "100%", width: "100%" }} />
                          </Stack>
                          <Stack>
                            <Typography fontSize={13} fontFamily={"Inter"} sx={{ color: "#64748B", fontWeight: 500 }}>
                              {field.field}
                            </Typography>
                            <Typography fontSize={15} sx={{ color: "#334155", fontWeight: 600 }}>
                              {field.access(row, invoiceDetailsQueryData?.references)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                    ))}
                    <Grid xs={2.4} key={"Rising Star Level"}>
                      <Tooltip title={`Rising Star Level for the year ${reservationDateYear}`} variant="outlined" arrow>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Stack
                            sx={{
                              width: "35px",
                              height: "35px",
                              backgroundColor: "#F1F5F9",
                              borderRadius: "10px",
                              padding: "8px",
                            }}
                          >
                            <StarIcon sx={{ height: "100%", width: "100%" }} />
                          </Stack>
                          <Stack>
                            <Typography fontSize={13} fontFamily={"Inter"} sx={{ color: "#64748B", fontWeight: 500 }}>
                              Rising Star
                            </Typography>

                            <Typography fontSize={15} sx={{ color: "#334155", fontWeight: 600 }}>
                              {effectiveRisingStarLevel !== undefined ? effectiveRisingStarLevel : "N/A"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Box sx={{ overflowX: "auto", scrollbarWidth: "thin", scrollbarColor: "#c1c1c1 transparent" }}>
                    <Table variant="plain" sx={{ padding: "1rem", tableLayout: "auto" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "center" }}>Type</th>
                          <th style={{ textAlign: "right" }}>Pub Fare</th>
                          <th style={{ textAlign: "right" }}>Sell Fare</th>
                          <th style={{ textAlign: "right" }}>Tax1</th>
                          <th style={{ textAlign: "right" }}>Oth. Taxes</th>
                          <th style={{ textAlign: "right" }}>HST</th>
                          <th style={{ textAlign: "right" }}>PST/QST</th>
                          <th style={{ textAlign: "right" }}>Gross Amt.</th>
                          <th style={{ textAlign: "right" }}>Comm. Amt.</th>
                          <th style={{ textAlign: "right" }}>Net Comm.</th>
                          <th style={{ textAlign: "right" }}>VAT/Comm.</th>
                          <th style={{ textAlign: "right" }}>
                            OS1 Comm.
                            <Tooltip
                              variant="outlined"
                              title="Rising Star Commission Percentage Based on Agency and Product Tier"
                              arrow
                            >
                              <span style={{ textDecoration: "underline" }}>
                                (
                                {(() => {
                                  const productTierObj = productTiers.find(
                                    (pt) => pt.vendorNumber === row.reservation?.vendorNumber,
                                  );
                                  const tier = resolveProductTier(productTierObj, row.reservation?.productCode);
                                  const key = tier.toLowerCase() as "preferred" | "approved" | "others";
                                  const value = risingStarMap[effectiveRisingStarLevel || 0]?.[key];
                                  return typeof value === "object" ? JSON.stringify(value) : value;
                                })()}
                                %)
                              </span>
                            </Tooltip>
                          </th>
                          <th style={{ textAlign: "right" }}>OS2 Comm.</th>
                          <th style={{ textAlign: "right" }}>Amt. Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.tableRows.map((row: any, index: number) => (
                          <tr
                            key={index}
                            style={{
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <td style={{ textAlign: "center" }}>{row.type}</td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.publishedFare) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.publishedFare)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.sellingFare) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.sellingFare)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.tax1 || 0) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.tax1 || 0)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.tax2 || 0) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.tax2 || 0)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.tax3 || 0) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.tax3 || 0)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.tax4 || 0) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.tax4 || 0)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.grossAmount) !== 0 ? 800 : 400,
                              }}
                            >
                              {row.grossAmount ? customCurrencyFormatter(row.grossAmount) : "-"}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.commissionAmount) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.commissionAmount)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {row.netCommission !== undefined ? customCurrencyFormatter(row.netCommission) : "-"}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.vatOnCommission) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.vatOnCommission)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.osAgentCommission1) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.osAgentCommission1)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.osAgentCommission2) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.osAgentCommission2)}
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: row.type === "Bal." && Number(row.payment?.amount || 0) !== 0 ? 800 : 400,
                              }}
                            >
                              {customCurrencyFormatter(row.payment?.amount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </Stack>
          );
        })}
      </>
    </>
  );
}
